import { useAuthContext } from "@hooks/contexts/auth-context";
import { getCampaignAffiliateReferral } from "@hooks/functions/Referral/storage";
import { requestHeaderLogin } from "@hooks/functions/Client/requestHeaderLogin";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaBoltLightning } from "react-icons/fa6";
import {
  FiLogIn,
  FiExternalLink,
  FiClock,
  FiShoppingCart,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import {
  ICheckOutDTO,
  IRaffleDefaultTicketCheckoutResponseDTO,
} from "./types";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: "linear-gradient(160deg, #fff 0%, #ECF4FF 60%, #D6E9FF 100%)",
  cardBg: "#ffffff",
  border: "#e4e8f0",
  headerBg: "#f8f9fc",
  text: "#111827",
  textMuted: "#6b7280",
  textLight: "#9ca3af",
  accentBg: "#eff4ff",
  accentBorder: "#bfdbfe",
  accentText: "#1d4ed8",
  btnBg: "#2563eb",
  btnDisabled: "#93c5fd",
  btnText: "#ffffff",
  totalBg: "#1e3a8a",
  totalText: "#ffffff",
  successBg: "#f0fdf4",
  successBorder: "#bbf7d0",
  successText: "#15803d",
  dangerText: "#b91c1c",
  warnBg: "#fffbeb",
  warnBorder: "#fcd34d",
  warnText: "#92400e",
  // Mercado Pago brand blue
  mpBg: "#009ee3",
  mpBgHover: "#0084c1",
};

// ─── Shared styles ─────────────────────────────────────────────────────────────
const cardStyle: React.CSSProperties = {
  background: C.cardBg,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
};

const cardHeaderStyle: React.CSSProperties = {
  background: C.headerBg,
  borderBottom: `1px solid ${C.border}`,
  padding: "13px 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: 48,
};

const cardHeaderLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: C.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  margin: 0,
};

// ─── Props & DTO ───────────────────────────────────────────────────────────────
interface IProps {
  raffleKey: string;
  tickets: string[];
  totalPrice: number;
  checkout?: ICheckOutDTO;
  onCheckoutChange: (checkout?: ICheckOutDTO) => void;
  onCheckoutPaid?: () => void;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
const masks = new Masks();

// ─── Component ─────────────────────────────────────────────────────────────────
export const CardPaymentCheckout = ({
  raffleKey,
  tickets,
  totalPrice,
  checkout,
  onCheckoutChange,
  onCheckoutPaid,
}: IProps) => {
  const { client } = useAuthContext();
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [paymentReady, setPaymentReady] = useState(Boolean(checkout?.trId));
  const [paymentPaid, setPaymentPaid] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(10);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [isDisabledButton, setIsDisabledButton] = useState(
    Boolean(checkout?.trId),
  );
  const paidToastShownRef = useRef(false);
  const checkoutErrorShownRef = useRef(false);

  function isCheckoutExpired(dateExp?: Date | string | null) {
    if (!dateExp) return true;
    return dayjs(dateExp).isBefore(dayjs());
  }

  const releaseCheckoutForNewPayment = useCallback((message?: string) => {
    onCheckoutChange(undefined);
    setPaymentReady(false);
    setPaymentPaid(false);
    setRedirectCountdown(10);
    setIsDisabledButton(false);
    paidToastShownRef.current = false;

    if (message && !checkoutErrorShownRef.current) {
      checkoutErrorShownRef.current = true;
      pushToastify({
        message,
        type: "warning",
        theme: "dark",
      });
    }
  }, [onCheckoutChange, pushToastify]);

  const loadCheckoutByTransaction = useCallback(async (trId: string) => {
    const response = await api.get<IRaffleDefaultTicketCheckoutResponseDTO>(
      "/raffleClient/default/ticket/checkout",
      { params: { trId } },
    );

    const checkoutData = response.data;
    onCheckoutChange({
      trId: checkoutData.trId,
      url: checkoutData.url,
      dateExp: checkoutData.dateExp,
      prices: checkoutData.prices,
    });

    setPaymentReady(Boolean(checkoutData.url));
    setIsDisabledButton(Boolean(checkoutData.url));
    setPaymentPaid(checkoutData.paid);

    if (checkoutData.paid && !paidToastShownRef.current) {
      paidToastShownRef.current = true;
      onCheckoutPaid?.();
      pushToastify({
        message: "Pagamento aprovado!",
        type: "success",
        theme: "dark",
      });
    }

    if (!checkoutData.paid && isCheckoutExpired(checkoutData.dateExp)) {
      releaseCheckoutForNewPayment(
        "O checkout expirou. Gere um novo pagamento para continuar.",
      );
    }
  }, [onCheckoutChange, onCheckoutPaid, pushToastify, releaseCheckoutForNewPayment]);

  useEffect(() => {
    setPaymentReady(Boolean(checkout?.trId));
    setIsDisabledButton(Boolean(checkout?.trId));
  }, [checkout?.trId]);

  useEffect(() => {
    if (!checkout?.trId || paymentPaid) return;

    checkoutErrorShownRef.current = false;
    loadCheckoutByTransaction(checkout.trId).catch((err: any) => {
      releaseCheckoutForNewPayment(
        err?.response?.data?.message ||
          "Não foi possível recuperar o checkout. Gere um novo pagamento.",
      );
    });

    const interval = window.setInterval(() => {
      loadCheckoutByTransaction(checkout.trId).catch((err: any) => {
        releaseCheckoutForNewPayment(
          err?.response?.data?.message ||
            "Não foi possível recuperar o checkout. Gere um novo pagamento.",
        );
      });
    }, 10000);

    return () => window.clearInterval(interval);
  }, [
    checkout?.trId,
    loadCheckoutByTransaction,
    paymentPaid,
    releaseCheckoutForNewPayment,
  ]);

  useEffect(() => {
    if (!paymentPaid) return;

    if (redirectCountdown <= 0) {
      navigate("/my-tickets");
      return;
    }

    const timeout = window.setTimeout(() => {
      setRedirectCountdown((prev) => prev - 1);
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [navigate, paymentPaid, redirectCountdown]);

  async function handleGeneratePayment() {
    if (!client) return;
    setIsDisabledButton(true);
    setLoadingPayment(true);

    try {
      const affiliate = getCampaignAffiliateReferral(raffleKey);
      const data = { key: raffleKey, totalPrice, tickets, affiliate };
      const response = await api.post(
        "/raffleClient/default/ticket/checkout",
        data,
      );
      const checkoutData = response?.data as ICheckOutDTO;
      onCheckoutChange(checkoutData);

      setPaymentReady(true);
      setIsDisabledButton(true);
      checkoutErrorShownRef.current = false;

      pushToastify({
        message: "Checkout criado com sucesso!",
        type: "success",
        theme: "dark",
      });
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ?? "Ocorreu um erro ao gerar o checkout!",
        type: "error",
        theme: "dark",
      });
      setIsDisabledButton(false);
    } finally {
      setLoadingPayment(false);
    }
  }

  // ─── Idle state (before payment is generated) ───────────────────────────────
  const IdleView = (
    <>
      {/* Mercado Pago badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "linear-gradient(135deg, #009ee3 0%, #0070ba 100%)",
          borderRadius: 10,
          padding: "12px 16px",
          width: "100%",
        }}
      >
        <FiShoppingCart
          style={{ color: "#fff", fontSize: 20, flexShrink: 0 }}
        />
        <div style={{ textAlign: "left" }}>
          <p
            style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#fff" }}
          >
            Mercado Pago
          </p>
          <p
            style={{ margin: 0, fontSize: 11, color: "rgba(255,255,255,0.8)" }}
          >
            Pagamento seguro e rápido
          </p>
        </div>
      </div>

      {/* Instruction */}
      <p
        style={{
          fontSize: 13.5,
          color: C.textMuted,
          maxWidth: 240,
          margin: 0,
          lineHeight: 1.65,
        }}
      >
        {client ? (
          <>
            Clique em <strong style={{ color: C.text }}>Gerar Pagamento</strong>{" "}
            para ser redirecionado ao checkout seguro.
          </>
        ) : (
          <>
            Você precisa <strong style={{ color: C.text }}>entrar</strong> para
            continuar com o pagamento.
          </>
        )}
      </p>

      {/* Total strip */}
      <div
        style={{
          width: "100%",
          background: C.totalBg,
          borderRadius: 8,
          padding: "14px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.7)",
            fontWeight: 500,
          }}
        >
          Total a pagar
        </span>
        <span style={{ fontSize: 20, fontWeight: 700, color: C.totalText }}>
          {masks.brlCurrency(totalPrice)}
        </span>
      </div>

      {/* Ticket count pill */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: C.accentBg,
          border: `1px solid ${C.accentBorder}`,
          borderRadius: 20,
          padding: "5px 12px",
        }}
      >
        <i
          className="bi bi-ticket-perforated"
          style={{ color: C.accentText, fontSize: 14 }}
        />
        <span style={{ fontSize: 12, fontWeight: 600, color: C.accentText }}>
          {tickets.length}{" "}
          {tickets.length === 1 ? "ticket selecionado" : "tickets selecionados"}
        </span>
      </div>

      {/* CTA button */}
      <button
        onClick={() => {
          if (!client) {
            requestHeaderLogin();
            return;
          }
          handleGeneratePayment();
        }}
        disabled={isDisabledButton}
        style={{
          width: "100%",
          padding: "14px 0",
          background: isDisabledButton
            ? C.btnDisabled
            : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          color: C.btnText,
          border: "none",
          borderRadius: 9,
          fontSize: 15,
          fontWeight: 600,
          cursor: isDisabledButton ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          boxShadow: isDisabledButton
            ? "none"
            : "0 4px 14px rgba(37,99,235,0.35)",
          transition: "all 0.2s ease",
        }}
      >
        {loadingPayment ? (
          <>
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
            Gerando pagamento...
          </>
        ) : client ? (
          <>
            <FaBoltLightning className="fs-5" />
            Gerar Pagamento
          </>
        ) : (
          <>
            <FiLogIn className="fs-5" />
            Logue p/ Continuar
          </>
        )}
      </button>

      {/* Security note */}
      <p
        style={{
          fontSize: 11.5,
          color: C.textLight,
          margin: 0,
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <i
          className="bi bi-shield-lock-fill"
          style={{ color: "#22c55e", fontSize: 13 }}
        />
        Ambiente seguro · SSL · Mercado Pago
      </p>
    </>
  );

  // ─── Success state (after payment is generated) ──────────────────────────────
  const SuccessView = (
    <>
      {/* Success alert */}
      <div
        style={{
          width: "100%",
          background: C.successBg,
          border: `1px solid ${C.successBorder}`,
          borderRadius: 8,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: C.successText,
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <i className="bi bi-check-circle-fill" style={{ fontSize: 17 }} />
        <span>
          {paymentPaid
            ? "Pagamento aprovado!"
            : checkout?.url
              ? "Checkout gerado com sucesso!"
              : "Consultando checkout..."}
        </span>
      </div>

      <p
        style={{
          fontSize: 13.5,
          color: C.textMuted,
          margin: 0,
          lineHeight: 1.65,
        }}
      >
        {paymentPaid ? (
          <>
            Vamos redirecionar você para seus tickets em{" "}
            <strong style={{ color: C.text }}>{redirectCountdown}</strong>{" "}
            segundos.
          </>
        ) : checkout?.url ? (
          <>
            Clique no botão abaixo para ser redirecionado ao ambiente seguro do{" "}
            <strong style={{ color: C.text }}>Mercado Pago</strong>.
          </>
        ) : (
          <>Estamos recuperando o checkout salvo para esta compra.</>
        )}
      </p>

      {/* Expiration notice */}
      {checkout?.dateExp && (
        <div
          style={{
            width: "100%",
            background: C.warnBg,
            border: `1px solid ${C.warnBorder}`,
            borderRadius: 8,
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            color: C.warnText,
            fontSize: 12.5,
            fontWeight: 500,
          }}
        >
          <FiClock style={{ fontSize: 15, flexShrink: 0 }} />
          <span>
            Expira às{" "}
            <strong>{dayjs(checkout?.dateExp).format("DD/MM/YY HH:mm")}</strong>
            .
          </span>
        </div>
      )}

      {/* Redirect button */}
      {!paymentPaid && checkout?.url && (
        <Link
          target="_blank"
          to={checkout?.url ?? "#"}
          className="text-decoration-none"
          style={{ width: "100%" }}
        >
          <button
            style={{
              width: "100%",
              padding: "14px 0",
              background: "linear-gradient(135deg, #009ee3 0%, #0070ba 100%)",
              color: "#fff",
              border: "none",
              borderRadius: 9,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: "0 4px 14px rgba(0,158,227,0.35)",
              transition: "all 0.2s ease",
            }}
          >
            <FiExternalLink style={{ fontSize: 17 }} />
            Ir para o Checkout
          </button>
        </Link>
      )}

      {/* Security note */}
      <p
        style={{
          fontSize: 11.5,
          color: C.textLight,
          margin: 0,
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <i
          className="bi bi-shield-lock-fill"
          style={{ color: "#22c55e", fontSize: 13 }}
        />
        {paymentPaid
          ? "Compra confirmada com segurança"
          : "Você será redirecionado com segurança"}
      </p>
    </>
  );

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ ...cardStyle, position: "sticky", top: 24 }}>
      {/* Card header */}
      <div style={cardHeaderStyle}>
        <span style={cardHeaderLabel}>Pagamento via CheckOut</span>
        {paymentReady && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: C.successText,
              background: C.successBg,
              border: `1px solid ${C.successBorder}`,
              borderRadius: 20,
              padding: "2px 10px",
            }}
          >
            ● {paymentPaid ? "Pago" : checkout?.url ? "Pronto" : "Consultando"}
          </span>
        )}
      </div>

      {/* Content */}
      <div
        style={{
          padding: "24px 20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
          textAlign: "center",
        }}
      >
        {paymentReady ? SuccessView : IdleView}
      </div>
    </div>
  );
};
